import * as babylon from "@babylonjs/core";
import { isEqual } from "lodash";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonState } from "../../state";

import {
  boundingRadiusOf,
  boundsOf,
  meshBounds,
  type Bounds,
} from "./meshBounds";

/**
 * Scene-derived radius envelope for the third-person camera. `max`
 * scales with the union of all entity bounding boxes — third-person has
 * no tracked entity, but the union captures the same scene scale so
 * zooming out reveals comparable content to what orbit would show.
 *
 * `max` uses a `boundingRadius * 41` multiplier, mirroring orbit-path's
 * `minRadius + maxRadius = (boundingRadius + 0.1) + boundingRadius * 40`.
 * Orbit-path is the right analogue: both modes are scoped to "the
 * spatial extent of content" rather than to "a single small object,"
 * so the multiplier should be calibrated to the scene's own extent, not
 * to 1000x it. The earlier `* 1000` was inherited from orbit-mesh
 * (where `boundingRadius` is a small followed entity) and produced
 * inflated `maxZ`, `maxDrawDistance`, and grid `totalLength` downstream
 * — see `ENG-2233/ENG-2233-perf-issues-investigation.md` findings #1,
 * #4, #8, and #10 for the analysis.
 *
 * `min` scales with scene content rather than sitting at orbit's
 * anti-intersection floor (`boundingRadius * 2 + 0.1`). Orbit's lower
 * bound keeps the camera outside the followed object's bounding sphere;
 * third-person's focus point is abstract, with no object to intersect.
 * The chosen floor `max(0.5, boundingRadius * 0.1)` prevents the camera
 * from approaching the focus point so closely that it appears to pass
 * through nearby content (or, with smoothing residue, through the focus
 * itself), while still allowing close-up framing of a small object
 * within a larger scene at roughly 10% of scene extent.
 *
 * Empty-scene fallback: `boundingRadius = 1`, yielding `[0.5, 41.5]`.
 *
 * Exported so `buildThirdPersonCamera` seeds the singleton `camera.zoom`
 * from the same source as the per-frame recomputation here.
 */
export function computeThirdPersonZoom(state: BabylonState): {
  min: number;
  max: number;
} {
  const generation = state.sceneConfig.entityGeneration ?? 0;
  const cache = state.sceneConfig.thirdPersonZoomCache;
  if (cache && cache.generation === generation) {
    return cache.value;
  }
  let union: Bounds | undefined;
  const extend = (bounds: Bounds | undefined) => {
    if (!bounds) {
      return;
    }
    union = union
      ? {
          min: babylon.Vector3.Minimize(union.min, bounds.min),
          max: babylon.Vector3.Maximize(union.max, bounds.max),
        }
      : { min: bounds.min.clone(), max: bounds.max.clone() };
  };

  // A model contributes the extent of its whole node hierarchy, which is what
  // `meshBounds` is for: the component is anchored to a `TransformNode`, and a
  // transform node has no bounding info to read. A model still loading has no
  // nodes yet and contributes nothing, which is why `entityGeneration` is bumped
  // when one lands.
  for (const entity of Object.values(state.entities)) {
    extend(meshBounds(entity.mesh));
    extend(boundsOf(entity.path?.mesh));
  }

  const boundingRadius = boundingRadiusOf(union);
  const min = Math.max(0.5, boundingRadius * 0.1);
  const max = min + boundingRadius * 41;
  const value = { min, max };
  state.sceneConfig.thirdPersonZoomCache = { value, generation };
  return value;
}

/**
 * The scene-derived limits the camera is held to: how close and how far it may
 * sit from its focus, and how far it draws.
 */
interface CameraEnvelope {
  zoom: { min: number; max: number };
  maxDrawDistance: number;
}

/**
 * The camera's envelope for the scene as it currently stands.
 *
 * Recomputed when the scene's *content* changes — `entityGeneration`, bumped by
 * `updateEntities`, by a model or node whose geometry lands, and by a path mesh
 * being built — or when the camera spec itself does. Not per frame, and that is
 * the whole point of the cache rather than an optimization.
 *
 * Every input here is a bounding box read off drawn geometry, and for a `model`
 * that box is the union of a *posed* hierarchy: a robot playing back its joints
 * changes it on every frame. Deriving the envelope from that live union made
 * each of its consumers oscillate at playback rate — `fogDensity` and
 * `babylonCamera.maxZ` rewritten every frame, and, because the grid is rebuilt
 * whenever the envelope moves, the whole grid disposed and re-meshed on every
 * 50ms reload poll. Measured on the four-joint sample arm: 40 grid rebuilds in
 * 40 polls, against 1 for the same model standing still.
 *
 * Scene scale is a property of what is in the scene, not of where its parts
 * happen to be this instant, so it is measured when the content changes. This is
 * what `computeThirdPersonZoom` already did for its own union; the orbit
 * envelope and the draw distance were the paths that still ran per frame, and a
 * rigid mesh's box does not move enough for that to have shown before models.
 */
function cameraEnvelope(
  state: BabylonState,
  camera: NonNullable<BabylonState["sceneConfig"]["camera"]>,
): CameraEnvelope {
  const generation = state.sceneConfig.entityGeneration ?? 0;
  const cached = state.sceneConfig.cameraEnvelope;
  if (
    cached &&
    cached.generation === generation &&
    // By identity: `syncCamera` replaces the spec object only when the panel
    // state's camera actually differs, so an unchanged config keeps this stable.
    cached.spec === camera.spec
  ) {
    return cached.value;
  }

  const zoom =
    camera.spec.type === schema.CAMERA_TYPE.thirdPerson
      ? computeThirdPersonZoom(state)
      : computeOrbitZoom(state, camera.spec);
  const value = {
    zoom,
    maxDrawDistance: computeMaxDrawDistance(state, zoom),
  };
  state.sceneConfig.cameraEnvelope = {
    value,
    generation,
    spec: camera.spec,
  };
  return value;
}

/**
 * Constrains the camera's zoom envelope, fog density, and depth
 * clipping each frame.
 *
 * `babylonCamera.target` stays at `Vector3.Zero()` for both camera
 * modes; the follow behavior comes from `updateCamera` writing
 * `camera.focusPoint` from the tracked entity (orbit) or user input
 * (third-person), and `applyRenderOrigin` shifting every mesh by
 * `-focusPoint` so the focus renders at the render-space origin.
 *
 * Zoom limits derive from scene scale: orbit reads the spec envelope
 * when given and otherwise infers from the followed entity's bounding
 * box; third-person infers from the union of all entity bounding
 * boxes. Draw distance and fog density derive from the resolved
 * envelope so the grid fades smoothly as it approaches the draw
 * distance. All of it comes from {@link cameraEnvelope}, which measures the
 * scene when its content changes rather than every frame — see there for why
 * that distinction is what keeps an articulated model from flickering the grid.
 */
export function constrainCamera(state: BabylonState) {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return;
  }
  camera.babylonCamera.update();

  const { zoom, maxDrawDistance } = cameraEnvelope(state, camera);
  // Formulas for fog transparency available here: https://learn.microsoft.com/en-us/previous-versions/windows/desktop/bb324452(v=vs.85)
  // Anything further than the distance where the fog becomes nearly opaque
  // is effectively not visible.
  const NEARLY_OPAQUE = 0.01;
  state.context.scene.fogDensity =
    Math.log(1 / NEARLY_OPAQUE) / maxDrawDistance;

  const babylonCamera = camera.babylonCamera;
  babylonCamera.minZ = zoom.min > 0 ? zoom.min / 10 : 0.1;
  babylonCamera.maxZ = Math.max(10000, zoom.max * 1000);
  babylonCamera.radius = Math.max(
    Math.min(babylonCamera.radius, zoom.max),
    zoom.min,
  );

  if (!isEqual(zoom, camera.zoom)) {
    // The computed zoom ranges and drawing distances have changed.
    camera.zoom = zoom;

    // TODO: broadcast this computed value to the react state
    // to be able to show the default value in the panel settings.

    // Update the grid to draw to the right size.
    if (state.sceneConfig.grid) {
      state.sceneConfig.grid.load.state = "stale";
    }
  }
}

/**
 * Resolves the maximum draw distance per camera mode.
 *
 * Orbit-`mesh` derives from the followed entity's bounding radius;
 * orbit-`path` from the trajectory's bounding radius (and uses a
 * tighter multiplier so fog tracks typical viewing distance along the
 * path). Third-person mirrors orbit-`mesh`'s formula against the
 * first entity with a mesh component — this gives the two modes
 * matching fog behavior for the same scene without coupling
 * third-person's fog to its (larger) scene-union zoom envelope.
 *
 * Empty-scene / no-mesh fallback: `boundingRadius = 1`, mirroring
 * `computeThirdPersonZoom`.
 *
 * `zoom` is the envelope resolved for this same measurement, taken as an
 * argument rather than read back off `camera.zoom` — that field still holds the
 * *previous* envelope at the point this runs, so reading it would derive the
 * orbit modes' fog from a distance the camera is no longer held to.
 *
 * The exhaustive switch forces a future camera type to declare its
 * own draw distance.
 */
export function computeMaxDrawDistance(
  state: BabylonState,
  zoom: { min: number; max: number },
): number {
  const camera = state.sceneConfig.camera!;
  const { spec } = camera;
  switch (spec.type) {
    case schema.CAMERA_TYPE.orbit:
      return spec.target.mesh === "mesh" ? zoom.max * 10 : zoom.max * 2;
    case schema.CAMERA_TYPE.thirdPerson: {
      // First entity with a mesh component, mirroring
      // `resolveFallbackEntity`. `Object.values()[0]` is iteration-
      // order-dependent and may not have a mesh, so iterate.
      let bounds: Bounds | undefined;
      let pathBounds: Bounds | undefined;
      for (const entity of Object.values(state.entities)) {
        if (entity.mesh) {
          bounds = meshBounds(entity.mesh);
          pathBounds = boundsOf(entity.path?.mesh);
          break;
        }
      }
      const boundingRadius = boundingRadiusOf(bounds);
      const pathRadius = pathBounds
        ? boundingRadiusOf(pathBounds)
        : boundingRadius;
      const minRadius = boundingRadius * 2 + 0.1;
      const maxRadius = Math.max(boundingRadius * 1000, pathRadius * 5);
      return (minRadius + maxRadius) * 10;
    }
    default: {
      const exhaustive: never = spec;
      throw new Error(
        `Unhandled camera type: ${(exhaustive as schema.Camera).type}`,
      );
    }
  }
}

function computeOrbitZoom(
  state: BabylonState,
  spec: schema.OrbitCamera,
): { min: number; max: number } {
  const target = spec.target;
  const tracked = state.entities[target.id];
  // The tracked thing is either the entity's whole mesh component — a primitive,
  // one glTF node, or a whole model — or its path trace.
  const targetBounds =
    target.mesh === "mesh"
      ? meshBounds(tracked?.mesh)
      : boundsOf(tracked?.path?.mesh);
  const targetPath = tracked?.path?.mesh;
  const boundingRadius = boundingRadiusOf(targetBounds);
  const pathRadius =
    target.mesh === "path" || !targetPath
      ? boundingRadius
      : boundingRadiusOf(boundsOf(targetPath));

  const minRadius =
    target.mesh === "mesh" ? boundingRadius * 2 + 0.1 : boundingRadius + 0.1;
  const maxRadius =
    target.mesh === "mesh"
      ? Math.max(boundingRadius * 1000, pathRadius * 5)
      : boundingRadius * 40;
  return {
    min: spec.zoom.min ?? minRadius,
    max: spec.zoom.max ?? minRadius + maxRadius,
  };
}
